import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { cn } from '../../lib/utils';
import DataWyseLogo from '../../assets/datawyse_logo_marian.svg';
import { usePriorityAccessModal } from '../../contexts/priority-access-modal-context';
import Analytics from '../../lib/analytics';

interface NavItem {
  label: string;
  href: string;
}

const navigationItems: NavItem[] = [
  { label: 'Product', href: '#product' },
  { label: 'Use Cases', href: '#use-cases' },
  { label: 'Getting Started', href: '#getting-started' },
  { label: 'FAQ', href: '#faq' },
];

interface NavbarProps {
  className?: string;
}

export const Navbar: React.FC<NavbarProps> = ({ className }) => {
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [bannerHidden, setBannerHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { openModal } = usePriorityAccessModal();

  // Check if we're on the landing page or other page types
  const isLandingPage = location.pathname === '/';
  const isContactOrCareersPage =
    location.pathname === '/contact' || location.pathname === '/careers';

  // Handle navigation clicks with smooth scrolling
  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    linkText: string
  ) => {
    e.preventDefault();

    // Track navigation click
    Analytics.trackNavigation('nav_link_click', {
      link_text: linkText,
      destination: href,
    });

    // Only handle scrolling on landing page
    if (!isLandingPage) {
      // For other pages, navigate to landing page with hash
      window.location.href = `/${href}`;
      return;
    }

    // Use custom scroll function for fixed container sections
    const targetId = href.replace('#', '');
    const scrollToSection = (window as any).scrollToSection;

    if (scrollToSection) {
      scrollToSection(targetId);
    } else {
      console.warn(`Scroll function not available for section: ${targetId}`);
    }
  };

  // Handle logo click
  const handleLogoClick = () => {
    Analytics.trackNavigation('nav_logo_click', {
      link_text: 'DataWyse Logo',
      destination: '/',
    });
  };

  // Handle CTA button click
  const handleCtaClick = () => {
    Analytics.trackNavigation('nav_cta_click', {
      link_text: 'Get Priority Access',
      destination: 'priority_access_modal',
    });
    openModal();
  };

  // Handle mobile menu toggle
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  // Handle mobile navigation clicks
  const handleMobileNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string,
    linkText: string
  ) => {
    setIsMobileMenuOpen(false); // Close menu after clicking
    handleNavClick(e, href, linkText);
  };

  useEffect(() => {
    const handleScroll = () => {
      // Only update scroll states for desktop screens to prevent mobile jerks
      if (window.innerWidth >= 600) {
        setIsScrolled(window.scrollY > 0);
        setBannerHidden(window.scrollY > 50);
      }
    };

    // Check initial scroll position
    handleScroll();

    // Only add scroll listeners on landing page for desktop
    if (isLandingPage && window.innerWidth >= 600) {
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    } else if (isContactOrCareersPage && window.innerWidth >= 600) {
      // On contact/careers pages, show non-pill style (reset state) - desktop only
      setIsScrolled(false);
      setBannerHidden(true);
    } else if (window.innerWidth >= 600) {
      // On other pages, show pill style - desktop only
      setIsScrolled(true);
      setBannerHidden(true);
    }

    // For mobile, ensure states remain static to prevent animation jerks
    if (window.innerWidth < 600) {
      setIsScrolled(false); // Keep mobile navbar in initial state
      setBannerHidden(false); // Banner always visible on mobile
    }
  }, [isLandingPage, isContactOrCareersPage]);

  return (
    <>
      {/* Main Navbar Wrapper */}
      <nav
        className={cn(
          'fixed right-0 left-0 z-50 w-full',
          'flex justify-center',
          'transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
          // Desktop behavior - UNCHANGED
          isContactOrCareersPage ? 'top-4' : bannerHidden ? 'top-4' : 'top-12',
          // Mobile override - directly below banner with minimal gap, no transitions (adjusted for taller banner)
          'max-[599px]:!top-11 max-[599px]:!transition-none',
          className
        )}
      >
        {/* Inner Container with Dynamic Width */}
        <div
          className={cn(
            'w-full transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]',
            // Desktop behavior - UNCHANGED
            isScrolled ? 'max-w-[70vw]' : 'max-w-[90vw]',
            // Mobile override - full width, no animation, no max-width constraints
            'max-[599px]:!max-w-none max-[599px]:!transition-none'
          )}
        >
          {/* Navbar Content Container */}
          <div
            className={cn(
              'flex w-full items-center justify-between',
              'px-6 py-4',
              'transition-all duration-[400ms] ease-[cubic-bezier(0.4,0,0.2,1)]',

              // Desktop conditions - COMPLETELY UNCHANGED
              isContactOrCareersPage
                ? [
                    'bg-white/30 backdrop-blur-[16px]',
                    'border border-white/40',
                    'rounded-full',
                    'shadow-[0_8px_32px_0_rgba(0,0,0,0.1)]',
                  ].join(' ')
                : isScrolled
                  ? [
                      'bg-white/30 backdrop-blur-[16px]',
                      'border border-white/40',
                      'rounded-full',
                      'shadow-[0_8px_32px_0_rgba(0,0,0,0.1)]',
                    ].join(' ')
                  : [
                      'bg-transparent',
                      'border-none',
                      'rounded-xl',
                      'shadow-none',
                    ].join(' '),

              // Mobile overrides - always styled, reduced padding, no transitions, no rounded corners
              'max-[599px]:!bg-white/30',
              'max-[599px]:!backdrop-blur-[16px]',
              'max-[599px]:!border',
              'max-[599px]:!border-white/40',
              'max-[599px]:!rounded-none', // Remove rounded corners for mobile
              'max-[599px]:!shadow-[0_8px_32px_0_rgba(0,0,0,0.1)]',
              'max-[599px]:!transition-none',
              'max-[599px]:px-4', // Add left/right padding for mobile
              'max-[599px]:py-3' // Increased vertical padding for mobile
            )}
          >
            {/* Logo */}
            <Link
              to="/"
              className="group flex items-center"
              onClick={handleLogoClick}
            >
              <img
                src={DataWyseLogo}
                alt="DataWyse Logo"
                className="h-10 max-[599px]:h-8"
              />
            </Link>

            {/* Desktop Navigation Links */}
            <nav className="hidden items-center gap-2 md:flex">
              {navigationItems.map(item => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={e => handleNavClick(e, item.href, item.label)}
                  className={cn(
                    'px-4 py-2',
                    'text-base font-medium text-[#4B5563]',
                    'transition-colors duration-200 ease-in-out',
                    'hover:text-[rgb(59,91,235)]',
                    'cursor-pointer'
                  )}
                >
                  {item.label}
                </a>
              ))}
            </nav>

            {/* Right Side Container */}
            <div className="flex items-center gap-4 max-[599px]:gap-2">
              {/* CTA Button - Hidden on mobile */}
              <button
                onClick={handleCtaClick}
                className={cn(
                  'inline-flex items-center',
                  'px-4 py-2.5',
                  'bg-[rgb(59,91,235)] text-white',
                  'rounded-lg',
                  'text-sm font-medium',
                  'transition-all duration-200 ease-in-out',
                  'hover:scale-[1.02] hover:bg-[rgb(79,111,255)]',
                  'focus:ring-2 focus:ring-[rgb(59,91,235)]/50 focus:ring-offset-2 focus:outline-none',
                  'max-[599px]:hidden'
                )}
              >
                Get Priority Access
              </button>

              {/* Mobile Hamburger Menu - Visible only on mobile */}
              <button
                onClick={toggleMobileMenu}
                className={cn(
                  'hidden items-center justify-center max-[599px]:flex',
                  'h-10 w-10',
                  'text-[#4B5563]',
                  'transition-colors duration-200',
                  'hover:text-[rgb(59,91,235)]'
                )}
                aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="fixed inset-0 z-40 hidden max-[599px]:block">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-black/20 backdrop-blur-sm"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Menu Content */}
          <div className="absolute top-20 right-4 left-4 rounded-xl border border-white/40 bg-white/95 shadow-xl backdrop-blur-md max-[599px]:top-[6.25rem] max-[599px]:right-0 max-[599px]:left-0 max-[599px]:rounded-none max-[599px]:border-l-0 max-[599px]:border-r-0">
            <div className="p-6">
              {/* Navigation Links */}
              <nav className="space-y-4">
                {navigationItems.map(item => (
                  <a
                    key={item.label}
                    href={item.href}
                    onClick={e =>
                      handleMobileNavClick(e, item.href, item.label)
                    }
                    className={cn(
                      'block px-4 py-3',
                      'text-lg font-medium text-[#4B5563]',
                      'transition-colors duration-200 ease-in-out',
                      'hover:bg-blue-50/50 hover:text-[rgb(59,91,235)]',
                      'cursor-pointer rounded-lg'
                    )}
                  >
                    {item.label}
                  </a>
                ))}

                {/* Get Priority Access button in mobile menu */}
                <div className="border-t border-gray-200/50 pt-4">
                  <button
                    onClick={() => {
                      setIsMobileMenuOpen(false);
                      handleCtaClick();
                    }}
                    className={cn(
                      'inline-flex w-full items-center justify-center',
                      'px-6 py-3',
                      'bg-[rgb(59,91,235)] text-white',
                      'rounded-lg',
                      'text-base font-semibold',
                      'transition-all duration-200 ease-in-out',
                      'hover:bg-[rgb(79,111,255)]'
                    )}
                  >
                    Get Priority Access
                  </button>
                </div>
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
